import React, { useState } from 'react';
import styles from 'pages/EventDetail.module.css';
import globalStyles from 'Global.module.css';
import { useQuery, useMutation } from '@apollo/client/react';
import { GetEventDetailDocument, GetEventDetailQuery } from 'graphql/queries.generated';
import { SetAttendanceDocument } from 'graphql/mutations.generated';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router';
import { useUser } from 'contexts/UserContext';
import { ImageOwnerType, ImageSize, Permission } from 'app-constants';
import Image from 'components/Image';
import { HasPermission } from 'components/HasPermission';
import { Link } from 'react-router';
import Map from 'components/Map';
import { formatLocation, formatEventDateTime } from 'utils';

const EventDetail: React.FC = () => {
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { user } = useUser();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [numGuests, setNumGuests] = useState(0);
    const [attending, setAttending] = useState(true);

    const { loading, error, data, refetch } = useQuery<GetEventDetailQuery>(GetEventDetailDocument, {
        variables: {
            eventId: eventId || ''
        },
        skip: !eventId
    });

    const [setAttendanceMutation, { loading: mutationLoading }] = useMutation(SetAttendanceDocument);

    const event = data?.event;

    const userOrganizationMemberId = !user || !event ? null :
        user.organizationPermissions.find(
            (perm) => perm.organizationId === event.organization.organizationId
        )?.organizationMemberId || null;

    const currentAttendance = !event || !userOrganizationMemberId ? null :
        event.attendees.find(
            (attendee) => attendee.organizationMember.organizationMemberId === userOrganizationMemberId
        );

    const isCurrentlyAttending = currentAttendance && currentAttendance.attending;

    const handleOpenDialog = () => {
        if (currentAttendance) {
            setNumGuests(currentAttendance.numGuests);
            setAttending(currentAttendance.attending);
        } else {
            setNumGuests(0);
            setAttending(true);
        }
        setIsDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!eventId || !userOrganizationMemberId) return;

        try {
            await setAttendanceMutation({
                variables: {
                    eventId,
                    organizationMemberId: userOrganizationMemberId,
                    attending,
                    numGuests
                }
            });
            await refetch();
            setIsDialogOpen(false);
        } catch (err) {
            console.error('Error setting attendence:', err);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    if (!event) {
        return <div>Event not found</div>;
    }

    return (
        <div className={styles.eventDetail}>

            <div className={globalStyles.shadowBox + ' ' + styles.header}>
                <div
                    className={styles.banner}
                    style={event.imageGradientColor ? { background: `linear-gradient(to bottom, ${event.imageGradientColor}, #FFFFFF)` } : undefined}
                >
                    <Image imageOwnerType={ImageOwnerType.Organization} imageSize={ImageSize.Banner} imageId={event.imageId} />
                </div>
                <div className={styles.headerDetails}>
                    <div className={styles.info}>
                        <p className={styles.dateText}>{formatEventDateTime(event.startTime, event.endTime)}</p>
                        <h1>{event.title}</h1>
                        <p>{formatLocation(event.location)}</p>
                    </div>
                    <div className={styles.eventButtons}>
                        {(event.attendeeLimit !== null && event.attendeeLimit < event.attendees.length) &&
                            <div className={styles.slotCount}>{event.attendeeLimit - event.attendees.length} slots left</div>
                        }
                        {userOrganizationMemberId && (
                            <div className={globalStyles.submitButton} onClick={handleOpenDialog}>
                                {isCurrentlyAttending ? 'Attending' : 'Attend'}
                            </div>
                        )}
                        <HasPermission groupId={event.organization.organizationId} permission={Permission.MANAGE_EVENTS}>
                            <div className={globalStyles.submitButton} onClick={() => navigate(`/events/${event.eventId}/edit`)}>Manage Event</div>
                        </HasPermission>
                    </div>
                </div>
            </div>
            <div className={styles.content}>
                <div className={styles.leftColumn}>
                    <div className={styles.eventHosts + ' ' + globalStyles.shadowBox}>
                        <div><h3>Hosted by:</h3></div>
                        {event.hosts.map((host) => (
                            <div key={host.organizationMemberId} className={styles.eventHost} onClick={() => navigate(`/groups/${event.organization.organizationId}/members/${host.organizationMemberId}`)}>
                                <Image imageOwnerType={ImageOwnerType.Person} imageSize={ImageSize.Thumbnail} imageId={host.combinedProfile.imageId} className={globalStyles.profileThumbnail} />
                                <span>{host.combinedProfile.username}</span>
                            </div>
                        ))}
                    </div>

                    <div className={styles.eventDescription + ' ' + globalStyles.shadowBox}>
                        <p>{event.description}</p>
                    </div>

                    <div className={styles.eventAttendees + ' ' + globalStyles.shadowBox}>
                        <div><h3>Attending:</h3></div>
                        {event.attendees.map((attendee) => (
                            <div key={attendee.eventAttendanceId}>
                                <Link to={`/groups/${event.organization.organizationId}/members/${attendee.organizationMember.organizationMemberId}`}>
                                    <Image
                                        imageOwnerType={ImageOwnerType.Person}
                                        imageSize={ImageSize.Thumbnail}
                                        imageId={attendee.organizationMember.combinedProfile.imageId}
                                        title={attendee.organizationMember.combinedProfile.username}
                                        className={globalStyles.profileThumbnail} />
                                </Link>
                            </div>
                        ))}
                    </div>

                    <div className={styles.eventTags + ' ' + globalStyles.shadowBox}>
                        <div><h3>Tags:</h3></div>
                        {event.tags.map((tag) => (
                            <div className={styles.tag}>{tag}</div>
                        ))}
                    </div>

                </div>
                <div className={styles.rightColumn}>
                    <div className={styles.hostingGroup + ' ' + globalStyles.shadowBox}>
                        <div className={styles.groupContainer} onClick={() => navigate(`/groups/${event.organization.organizationId}`)}>
                            <div className={styles.groupLogo}>
                                <Image imageOwnerType={ImageOwnerType.Organization} imageSize={ImageSize.Card} imageId={event.organization.imageId} />
                            </div>
                            <div className={styles.groupInfo}>
                                <p>{event.organization.name} </p>
                                <p>{event.organization.memberCount} members</p>
                                <p>{event.organization.private ? 'private group' : 'public group'}</p>
                            </div>
                        </div>
                    </div>
                    <div className={styles.eventLocation + ' ' + globalStyles.shadowBox}>
                        <div className={styles.locationPicture} >
                            <Map location={event.location} />
                        </div>
                        <div>{formatLocation(event.location)}</div>
                    </div>
                </div>
            </div>

            {isDialogOpen && (
                <div className={styles.dialogOverlay} onClick={() => setIsDialogOpen(false)}>
                    <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
                        <h2>{isCurrentlyAttending ? 'Update Attendance' : 'Set Attendance'}</h2>

                        {isCurrentlyAttending && (
                            <div className={styles.attendingToggle}>
                                <label>
                                    <input
                                        type="radio"
                                        name="attending"
                                        checked={attending}
                                        onChange={() => setAttending(true)}
                                    />
                                    Going
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        name="attending"
                                        checked={!attending}
                                        onChange={() => setAttending(false)}
                                    />
                                    Not Going
                                </label>
                            </div>
                        )}
                        {event.maxGuests}
                        <div className={styles.formGroup}>
                            <label htmlFor="numGuests">Number of Guests:</label>
                            <input
                                id="numGuests"
                                type="number"
                                min="0"
                                max={event.maxGuests || 0}
                                value={numGuests}
                                onChange={(e) => setNumGuests(parseInt(e.target.value) || 0)}
                            />
                        </div>

                        <div className={styles.dialogButtons}>
                            <button
                                onClick={handleSubmit}
                                disabled={mutationLoading}
                                className={globalStyles.submitButton}
                            >
                                {isCurrentlyAttending ? 'Update' : 'Submit'}
                            </button>
                            <button
                                onClick={() => setIsDialogOpen(false)}
                                className={globalStyles.cancelButton}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventDetail;
